import { useCallback, useEffect, useMemo, useRef } from 'react';

const stripLeadingSectionNumber = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value
    .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
    .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
    .replace(/^\s*\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
    .replace(/^\s*\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
    .trim();
};

const getGroupKey = (activity) => {
  const name = typeof activity?.section_name === 'string' ? activity.section_name.trim() : '';
  const number = typeof activity?.section_number === 'string' ? activity.section_number.trim() : '';
  if (name) return `name:${name}`;
  if (number) return `num:${number}`;
  return 'other';
};

const normalizeSectionNumberKey = (value) => String(value ?? '').trim();

export default function useLearningPackState({
  onShowLearningPack,
  learningPackActivities,
  sections,
  currentPage,
  currentSection,
  currentSectionIndex,
  onSectionScroll,
}) {
  const hasLearningPack =
    !!onShowLearningPack && Array.isArray(learningPackActivities) && learningPackActivities.length > 0;

  const learningPackSections = useMemo(() => {
    if (!Array.isArray(learningPackActivities) || learningPackActivities.length === 0) return [];

    const sectionNameByNumber = new Map();
    if (Array.isArray(sections)) {
      sections.forEach((sec) => {
        const sn = normalizeSectionNumberKey(sec?.sectionNumber ?? sec?.section_number);
        const title = String(sec?.title ?? sec?.fullTitle ?? '').trim();
        if (sn && title) sectionNameByNumber.set(sn, title);
      });
    }

    const sectionStartPageFromPdf = new Map();
    if (Array.isArray(sections)) {
      sections.forEach((sec) => {
        const sn = normalizeSectionNumberKey(sec?.sectionNumber ?? sec?.section_number);
        if (!sn) return;
        const rawPage = sec?.pageNumber ?? sec?.page_number;
        const pn = parseInt(rawPage, 10);
        if (Number.isFinite(pn) && pn > 0) sectionStartPageFromPdf.set(sn, pn);
      });
    }

    const byKey = new Map();
    for (const activity of learningPackActivities) {
      const rawName = activity?.section_name ?? activity?.sectionName ?? '';
      const name = typeof rawName === 'string' ? rawName.trim() : '';
      const sn = normalizeSectionNumberKey(activity?.section_number ?? activity?.sectionNumber);

      let groupKey = 'other';
      let resolvedTitle = name || sn || 'Section';

      if (sn) {
        const metadataName = sectionNameByNumber.get(sn);
        if (metadataName) {
          groupKey = `name:${metadataName}`;
          resolvedTitle = metadataName;
        } else if (name) {
          groupKey = `name:${name}`;
        } else {
          groupKey = `num:${sn}`;
        }
      } else if (name) {
        groupKey = `name:${name}`;
      }

      if (groupKey === 'other') continue;

      const rawPage = activity?.page_number ?? activity?.pageNumber;
      const pn = parseInt(rawPage, 10);

      if (!byKey.has(groupKey)) {
        byKey.set(groupKey, {
          groupKey,
          sectionNumber: sn || name,
          title: resolvedTitle,
          minActivityPage: Number.isFinite(pn) && pn > 0 ? pn : null,
        });
        continue;
      }

      const curr = byKey.get(groupKey);
      if (Number.isFinite(pn) && pn > 0) {
        if (!curr.minActivityPage || pn < curr.minActivityPage) curr.minActivityPage = pn;
      }
    }

    const out = Array.from(byKey.values())
      .map((entry) => {
        const startPage =
          (entry.sectionNumber && sectionStartPageFromPdf.get(entry.sectionNumber)) ??
          entry.minActivityPage ??
          1;
        return {
          groupKey: entry.groupKey,
          sectionNumber: entry.sectionNumber,
          title: entry.title,
          startPage,
        };
      })
      .filter((s) => !!s.groupKey)
      .sort((a, b) => {
        if (a.startPage !== b.startPage) return a.startPage - b.startPage;
        if (a.sectionNumber && b.sectionNumber) {
          return String(a.sectionNumber).localeCompare(String(b.sectionNumber), undefined, { numeric: true, sensitivity: 'base' });
        }
        return String(a.title || '').localeCompare(String(b.title || ''));
      });

    console.log(`📚 PDFSectionViewer: Built ${out.length} learningPackSections:`,
      out.map(s => `${s.sectionNumber}: ${s.title} (page ${s.startPage})`)
    );

    return out;
  }, [learningPackActivities, sections]);

  const activeLearningPackSection = useMemo(() => {
    if (!hasLearningPack || learningPackSections.length === 0) return null;
    const page = Number.isFinite(parseInt(currentPage, 10)) ? parseInt(currentPage, 10) : 1;

    const candidates = learningPackSections.filter(sec => (sec?.startPage || 0) <= page);

    console.log(`📍 Page ${page}: Found ${candidates.length} candidate sections:`,
      candidates.map(c => `${c.sectionNumber}: ${c.title} (starts p${c.startPage})`)
    );

    if (candidates.length === 0) return null;

    const samePage = candidates.filter(c => c.startPage === page);

    let selected;
    if (samePage.length > 1) {
      const titles = samePage.map(s => s.title).filter(Boolean);
      const sectionNumbers = samePage.map(s => s.sectionNumber).filter(Boolean);

      selected = {
        ...samePage[samePage.length - 1],
        title: titles.length === 2
          ? `${titles[0]} & ${titles[1]}`
          : titles.length > 2
            ? `${titles[0]} & ${titles.length - 1} more`
            : (titles[0] || 'Multiple Sections'),
        sectionNumber: sectionNumbers.join(' & '),
        groupKey: samePage.map(s => s.groupKey).join('_'),
        _isMerged: true,
        _mergedSections: samePage
      };
    } else {
      selected = candidates[candidates.length - 1] || null;
    }

    console.log(`✅ Page ${page}: Selected section: ${selected?.sectionNumber} - ${selected?.title}`);
    return selected;
  }, [hasLearningPack, learningPackSections, currentPage]);

  const activeLearningPackEndPage = useMemo(() => {
    if (!activeLearningPackSection || learningPackSections.length === 0) return null;
    const startPage = activeLearningPackSection.startPage || 0;
    for (const sec of learningPackSections) {
      const pn = sec?.startPage || 0;
      if (pn > startPage) return pn;
    }
    return null;
  }, [activeLearningPackSection, learningPackSections]);

  const prevActiveSectionRef = useRef(null);
  useEffect(() => {
    if (!activeLearningPackSection || !onSectionScroll) return;

    const currentKey = activeLearningPackSection.groupKey;
    if (prevActiveSectionRef.current !== currentKey) {
      prevActiveSectionRef.current = currentKey;
      onSectionScroll({
        sectionName: activeLearningPackSection.title,
        sectionNumber: activeLearningPackSection.sectionNumber,
        sectionKey: currentKey,
        isMerged: activeLearningPackSection._isMerged || false,
      });
    }
  }, [activeLearningPackSection, onSectionScroll]);

  const pageOrderedSections = useMemo(() => {
    if (!Array.isArray(sections) || sections.length === 0) return [];
    const normalized = [];
    for (let i = 0; i < sections.length; i += 1) {
      const sec = sections[i];
      const raw = sec?.pageNumber ?? sec?.page_number;
      const pn = parseInt(raw, 10);
      if (!Number.isFinite(pn) || pn <= 0) continue;
      normalized.push({ originalIndex: i, pageNumber: pn, section: sec });
    }
    normalized.sort((a, b) => a.pageNumber - b.pageNumber);
    return normalized;
  }, [sections]);

  const learningPackCountForCurrentSection = useMemo(() => {
    if (!hasLearningPack || !activeLearningPackSection) return 0;

    const targetGroupKey = activeLearningPackSection.groupKey;
    const isMerged = activeLearningPackSection._isMerged;
    const mergedGroupKeys = isMerged
      ? (activeLearningPackSection._mergedSections || []).map(s => s.groupKey)
      : [targetGroupKey];

    return learningPackActivities.reduce((acc, activity) => {
      const activityKey = getGroupKey(activity);
      if (mergedGroupKeys.includes(activityKey)) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [hasLearningPack, learningPackActivities, activeLearningPackSection]);

  const learningPackTitle =
    String(activeLearningPackSection?.title || '').trim() ||
    stripLeadingSectionNumber(currentSection?.fullTitle || 'Current Section');

  const currentSectionTitle = currentSection?.fullTitle
    ? stripLeadingSectionNumber(currentSection.fullTitle)
    : '';

  const handleLearningPackClick = useCallback(() => {
    if (!onShowLearningPack) return;

    const inferredStartPage = activeLearningPackSection?.startPage;
    const startPageRaw = inferredStartPage ?? currentSection?.pageNumber ?? currentSection?.page_number;
    const startPage = Number.isFinite(parseInt(startPageRaw, 10)) ? parseInt(startPageRaw, 10) : null;

    let endPage = null;
    if (Number.isFinite(parseInt(activeLearningPackEndPage, 10))) {
      endPage = parseInt(activeLearningPackEndPage, 10);
    } else if (startPage && pageOrderedSections.length > 0) {
      const pageIdx = pageOrderedSections.findIndex(
        (s) => s.originalIndex === currentSectionIndex && s.pageNumber === startPage
      );
      const startFrom = pageIdx >= 0 ? pageIdx + 1 : 0;
      for (let i = startFrom; i < pageOrderedSections.length; i++) {
        const pn = pageOrderedSections[i]?.pageNumber || 0;
        if (pn > startPage) {
          endPage = pn;
          break;
        }
      }
    }

    const sectionTitle =
      String(activeLearningPackSection?.title || '').trim() ||
      stripLeadingSectionNumber(currentSection?.fullTitle || currentSection?.title || '');

    onShowLearningPack(true, {
      sectionNumber: activeLearningPackSection?.sectionNumber || currentSection?.sectionNumber || null,
      sectionKey: activeLearningPackSection?.groupKey || null,
      startPage,
      endPage,
      sectionTitle,
      _isMerged: activeLearningPackSection?._isMerged || false,
      _mergedSections: activeLearningPackSection?._mergedSections || null,
    });
  }, [
    onShowLearningPack,
    activeLearningPackSection,
    currentSection,
    activeLearningPackEndPage,
    pageOrderedSections,
    currentSectionIndex,
  ]);

  return {
    hasLearningPack,
    learningPackCountForCurrentSection,
    learningPackTitle,
    currentSectionTitle,
    handleLearningPackClick,
  };
}
